import React, { useEffect, useState } from "react";
import { adminApi } from "../../apiAdmin";
import { DjUser, FeeTier } from "./types";
import { SHOW_TIERS } from "../../lib/tiersPaused";
import { minPriceFor, minRequestFor } from "../../utils/payinConfig";
import { currencySymbol } from "../../utils/currency";

// Per-DJ commission editor: the value of the Commission row in the Money
// section of the admin talent card (TalentCard.tsx).
// The admin types a PERCENTAGE; blank = "no override". What "no override"
// MEANS depends on the fee-tiers flag: the loyalty-ladder rate when tiers
// are live (feeTier.enabled — the copy names the DJ's actual tier), the
// legacy standard 15% when the flag is confirmed OFF, and a generic
// "auto · loyalty tier" while the tier payload is still loading (never
// flash a number we haven't confirmed). While Tier Level is paused (server
// says paused: true) "no override" is the standard 10%, and a lower override
// still wins (founder 2026-10-22). Saves via
// PATCH /users/:id/commission and hands the updated user back so the card
// refreshes. Mirrors the server contract: stored as a fraction, blank clears.
export function CommissionEditor({
  dj,
  feeTier,
  onSaved,
}: {
  dj: DjUser;
  feeTier: FeeTier | null;
  onSaved: (u: DjUser) => void;
}) {
  const ladderLive = !!(feeTier?.enabled && feeTier.tier);
  // No tier names while Tier Level is hidden (lib/tiersPaused) — just the rate.
  const ladderLabel = ladderLive ? (SHOW_TIERS ? `${feeTier!.tier!.name} ${feeTier!.tier!.feePct}%` : `${feeTier!.tier!.feePct}%`) : null;
  // Tiers paused on the server: everyone's standard rate is Bronze 10%.
  const paused = !!feeTier?.paused;
  // Payload arrived AND says the flag is off (not paused) — only then is "15%" the truth.
  const flagConfirmedOff = feeTier != null && !feeTier.enabled && !paused;
  // Stored fraction → percent for display (0.10 → "10"); blank when unset.
  const currentPercent =
    dj.commissionRate != null ? String(+(dj.commissionRate * 100).toFixed(4)) : "";
  const [draft, setDraft] = useState<string>(currentPercent);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  // Re-sync if the underlying dj changes (e.g. after our own save).
  useEffect(() => {
    setDraft(currentPercent);
  }, [currentPercent]);

  const dirty = draft.trim() !== currentPercent;

  const save = async () => {
    setBusy(true);
    try {
      const trimmed = draft.trim();
      const { data } = await adminApi.patch(`/users/${dj._id}/commission`, {
        // Blank clears the override back to the standard rate; otherwise send
        // the typed percentage and let the server convert + validate.
        commissionRate: trimmed === "" ? null : Number(trimmed),
      });
      onSaved(data.user);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err: any) {
      alert(err?.response?.data?.msg || "Could not update commission");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span
      className="ac-comm"
      title={
        ladderLive
          ? `Manual commission override. Leave blank to charge the loyalty ladder — currently ${ladderLabel} for this talent. A lower override always wins; a higher one is ignored while their tier is cheaper.`
          : paused
          ? "Platform commission for this talent. Leave blank for the standard 10%. A lower rate always wins; a higher one is ignored."
          : flagConfirmedOff
          ? "Platform commission for this talent. Leave blank for the standard 15%."
          : "Platform commission for this talent. Leave blank for the automatic rate (loyalty tier)."
      }
    >
      <input
        type="number"
        min={0}
        max={90}
        step={0.5}
        inputMode="decimal"
        aria-label="Commission %"
        value={draft}
        placeholder={ladderLive ? String(feeTier!.tier!.feePct) : paused ? "10" : flagConfirmedOff ? "15" : "auto"}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty && !busy) save();
        }}
        className="ac-input"
      />
      <span>%</span>
      {dirty ? (
        <button type="button" className="adm-btn-sm adm-btn-blue-sm ac-btn" disabled={busy} onClick={save}>
          {busy ? "…" : "Save"}
        </button>
      ) : saved ? (
        <span className="ac-saved">Saved</span>
      ) : !currentPercent ? (
        <span className="ac-muted ac-xs">
          {ladderLive ? `ladder: ${ladderLabel}` : paused ? "standard 10%" : flagConfirmedOff ? "standard 15%" : "auto · loyalty tier"}
        </span>
      ) : null}
    </span>
  );
}

// Per-DJ minimum request exception (founder 2026-10-30: "if a DJ DMs us… we
// should be able to manually change their min request"). Blank = the
// standard minimum for their currency; a number lowers it, from the $2
// class up to the standard (the server refuses anything else). Withdrawals
// keep the standard line. PATCH /users/:id/min-request (superadmin).
export function MinRequestEditor({ dj, onSaved }: { dj: DjUser; onSaved: (u: DjUser) => void }) {
  const currency = String(dj.currency || "usd").toLowerCase();
  const standard = minRequestFor(currency);
  const lowest = minPriceFor(currency);
  const current = dj.minRequestPriceOverride != null ? String(dj.minRequestPriceOverride) : "";
  const [draft, setDraft] = useState<string>(current);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setDraft(current);
  }, [current]);
  const dirty = draft.trim() !== current;
  const save = async () => {
    setBusy(true);
    try {
      const trimmed = draft.trim();
      const { data } = await adminApi.patch(`/users/${dj._id}/min-request`, { value: trimmed === "" ? null : Number(trimmed) });
      onSaved({ ...dj, ...data.user });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err: any) {
      alert(err?.response?.data?.msg || "Could not update the minimum");
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="ac-comm" title={`Lowest price this talent may charge per request. Leave blank for the standard ${currencySymbol(currency)}${standard}; you can go down to ${currencySymbol(currency)}${lowest}.`}>
      <span>{currencySymbol(currency)}</span>
      <input
        type="number"
        min={lowest}
        max={standard}
        step={currency === "usd" || currency === "eur" || currency === "gbp" || currency === "cad" || currency === "chf" ? 0.5 : 1}
        inputMode="decimal"
        aria-label="Minimum request"
        value={draft}
        placeholder={String(standard)}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty && !busy) save();
        }}
        className="ac-input"
      />
      {dirty ? (
        <button type="button" className="adm-btn-sm adm-btn-blue-sm ac-btn" disabled={busy} onClick={save}>
          {busy ? "…" : "Save"}
        </button>
      ) : saved ? (
        <span className="ac-saved">Saved</span>
      ) : !current ? (
        <span className="ac-muted ac-xs">standard</span>
      ) : (
        <span className="ac-muted ac-xs">exception</span>
      )}
    </span>
  );
}

// Fraud-door trust switches — the two decisions the founder
// makes once they know a talent is a real DJ, both audit-logged server-side:
//   • Caps: every account runs on the tight default price/boost/counter
//     ceilings; "Lift caps" moves this talent to the roomier pre-fraud-door
//     numbers (PATCH /users/:id/caps — reversible).
//   • Payout hold: new accounts hold earnings 7 days before payout (Stripe
//     delay_days); "Lift hold" drops it to Stripe's country minimum
//     (PATCH /users/:id/payout-hold — one-way from here).
// All three are superadmin only on the server (2026-10-22); every change asks first.
export type TrustBusy = "caps" | "hold" | "trust" | null;

export function useTrustActions(dj: DjUser, onSaved: (patch: Partial<DjUser>) => void) {
  const [busy, setBusy] = useState<TrustBusy>(null);

  const toggleCaps = async () => {
    const msg = dj.capsLifted
      ? `Put ${dj.name} back on the default price caps?`
      : `Lift ${dj.name}'s price caps? This only changes how much fans can bid — it doesn't make them trusted.`;
    if (!window.confirm(msg)) return;
    setBusy("caps");
    try {
      const { data } = await adminApi.patch(`/users/${dj._id}/caps`, { lifted: !dj.capsLifted });
      onSaved({ capsLifted: !!data?.user?.capsLifted });
    } catch (err: any) {
      alert(err?.response?.data?.msg || "Could not update caps");
    } finally {
      setBusy(null);
    }
  };

  const liftHold = async () => {
    if (!window.confirm(`Drop ${dj.name}'s payout hold to Stripe's minimum? This can't be undone from here.`)) return;
    setBusy("hold");
    try {
      const { data } = await adminApi.patch(`/users/${dj._id}/payout-hold`, {});
      onSaved({ payoutHoldDays: data?.payoutHoldDays ?? null, payoutHoldLiftedAt: new Date().toISOString() });
    } catch (err: any) {
      alert(err?.response?.data?.msg || "Could not lift the payout hold");
    } finally {
      setBusy(null);
    }
  };

  const toggleTrust = async () => {
    const next = !dj.trustedAt;
    const msg = next
      ? `Mark ${dj.name} as a trusted DJ? This lifts their caps and payout hold in one go, and they skip the first-cashout check.`
      : `Remove ${dj.name}'s trusted status? Their caps go back to the default. Their payout hold stays where it is — use Freeze to stop cash-outs.`;
    if (!window.confirm(msg)) return;
    setBusy("trust");
    try {
      const { data } = await adminApi.patch(`/users/${dj._id}/trust`, { trusted: next });
      onSaved({
        trustedAt: data?.user?.trustedAt ?? null,
        capsLifted: !!data?.user?.capsLifted,
        payoutHoldDays: data?.payoutHoldDays ?? dj.payoutHoldDays ?? null,
        payoutHoldLiftedAt: data?.payoutHoldLiftedAt ?? dj.payoutHoldLiftedAt ?? null,
      });
      if (data?.holdError) alert(data.msg);
    } catch (err: any) {
      alert(err?.response?.data?.msg || "Could not update trust");
    } finally {
      setBusy(null);
    }
  };

  return { busy, toggleCaps, liftHold, toggleTrust };
}

type Actions = ReturnType<typeof useTrustActions>;

// Each value sits on the right of its labelled row in the talent card
// (TalentCard.tsx); the row's title keeps the long explanation.

// Approve by hand: the way to approve a talent who isn't on the Approvals list
// (no verified Instagram, and the founder knows them). The page the review
// email's Approve button opens: one confirm tap, and it says if the bank isn't
// connected yet (pages/AdminReviewPage).
export function ApprovalValue({ dj }: { dj: DjUser }) {
  if (dj.isActivated) return <span>Approved</span>;
  return (
    <span className="ac-pair">
      <span>{dj.declinedAt ? "Declined" : "Not yet"}</span>
      <button type="button" className="adm-btn-sm adm-btn-blue-sm ac-btn" onClick={() => window.location.assign(`/admin/review/${encodeURIComponent(dj._id)}?do=approve`)}>
        Approve
      </button>
    </span>
  );
}

export function TrustedValue({ dj, a }: { dj: DjUser; a: Actions }) {
  return (
    <span className="ac-pair">
      <span>{dj.trustedAt ? `Yes · since ${new Date(dj.trustedAt).toLocaleDateString()}` : "No"}</span>
      <button type="button" className="adm-btn-sm adm-btn-blue-sm ac-btn" disabled={a.busy === "trust"} onClick={a.toggleTrust}>
        {a.busy === "trust" ? "…" : dj.trustedAt ? "Remove trust" : "Mark as trusted"}
      </button>
    </span>
  );
}

export function CapsValue({ dj, a }: { dj: DjUser; a: Actions }) {
  return (
    <span className="ac-pair">
      <span>{dj.capsLifted ? "Lifted" : "Default"}</span>
      <button type="button" className="adm-btn-sm adm-btn-blue-sm ac-btn" disabled={a.busy === "caps"} onClick={a.toggleCaps}>
        {a.busy === "caps" ? "…" : dj.capsLifted ? "Restore default" : "Lift caps"}
      </button>
    </span>
  );
}

export function PayoutHoldValue({ dj, a }: { dj: DjUser; a: Actions }) {
  const days = typeof dj.payoutHoldDays === "number" ? `${dj.payoutHoldDays} days` : "—";
  if (dj.payoutHoldLiftedAt) {
    return (
      <span className="ac-pair">
        <span>{days}</span>
        {/* Lifted, but Stripe still holds 7 days: that's Stripe's own minimum
            for this account (Canada, France, Belgium… on 2026-10-22), not ours. */}
        <span className="ac-muted ac-xs">{(dj.payoutHoldDays ?? 0) >= 7 ? "Stripe's minimum — ours is lifted" : "lifted"}</span>
      </span>
    );
  }
  if (dj.stripeAccountId) {
    return (
      <span className="ac-pair">
        <span>{days}</span>
        <button type="button" className="adm-btn-sm adm-btn-blue-sm ac-btn" disabled={a.busy === "hold"} onClick={a.liftHold}>
          {a.busy === "hold" ? "…" : "Lift hold"}
        </button>
      </span>
    );
  }
  return (
    <>
      {days}
      <span className="ac-muted ac-xs">no Stripe account yet</span>
    </>
  );
}
